import { updateSalesTaxProduct, useSalesTaxProduct } from '@commenda/api';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Icons,
} from '@commenda/kakashi';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import mixpanel from '@commenda/mixpanel';
import { useState } from 'react';
import SalesTaxProductConfirmationModal from './product-action-confirmation-modal';

interface SalesTaxProductTaxCodeEditModalProps {
  id: string;
  isProductTaxCodeOpen: boolean;
  setIsProductTaxCode: () => void;
  setNotification: (TNotification) => void;
  taxCode: string;
  onConfirm: () => void;
  onCancel: () => void;
  corporationId: number;
  formData: SalesTaxProductFormData;
}

interface SalesTaxProductFormData {
  name?: string;
  description?: string;
  tax_code?: string;
}

const SalesTaxProductTaxCodeEditModal = ({
  id,
  isProductTaxCodeOpen,
  setIsProductTaxCode,
  onConfirm,
  onCancel,
  corporationId,
  taxCode,
  setNotification,
  formData,
}: SalesTaxProductTaxCodeEditModalProps) => {
  const [isConfirmationModalOpen, setIsConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, error, mutate } = useSalesTaxProduct({
    corporationId: +corporationId,
    productId: id,
    token: undefined,
  });

  const handleApplyChange = async () => {
    const res = await updateSalesTaxProduct(
      {
        name: formData.name,
        description: formData.description,
        ...(taxCode ? { tax_code: taxCode } : null),
      },
      undefined,
      id,
      corporationId
    );

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: res.error,
      });
      setIsSubmitting(false);
      return;
    }
    setIsConfirmationModal(true);
    mixpanel.track(MIXPANEL_EVENTS.PRODUCT_MAPPED, {
      surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
      type: 'single',
    });
    onConfirm();
  };

  const handleCancel = () => {
    setIsProductTaxCode();
    onCancel();
  };

  return (
    <>
      <SalesTaxProductConfirmationModal
        title="Products details changed successfully!"
        message="This change will only apply to future transactions. Existing transactions remain unaffected."
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModal={setIsConfirmationModal}
        handleConfirmation={() => {
          setIsConfirmationModal(false);
        }}
      />

      <Dialog open={isProductTaxCodeOpen} onOpenChange={setIsProductTaxCode}>
        <DialogContent className="bg-white w-full flex flex-col">
          <DialogHeader className="flex items-start border-0 gap-4 py-6">
            <div className="bg-bg-warning flex items-center justify-center h-12 w-12 rounded-lg">
              <Icons.AlertCircle className="text-text-warning h-5 w-5" />
            </div>
            <div className="flex flex-col w-full items-start">
              <h3 className="text-text-primary txt-large-bold">
                Confirm product tax code update
              </h3>
              <p className="txt-small-regular text-text-tertiary">
                {`You're about to change the product tax code.`}
              </p>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-6 px-6 pt-3 pb-6">
            <p className="txt-small-regular text-text-tertiary ">
              All future transactions will use the new product tax code for tax
              calculations. Your existing transactions will not be affected.
            </p>
            <div className="flex flex-col sm:flex-row items-center w-full gap-1">
              <div className="bg-bg-secondary w-full rounded-md sm:w-[200px] p-3 flex flex-col gap-3">
                <h4 className="txt-small-bold text-text-secondary">
                  Previous product tax code
                </h4>
                <Badge size="sm" className="max-w-[174px] truncate">
                  {data?.productData?.taxCode || 'N/A'}
                </Badge>
              </div>
              <div className="hidden sm:flex w-[60px] items-center justify-center">
                <Icons.ArrowNarrowRight className="text-fg-quinary" />
              </div>
              <div className="bg-bg-secondary w-full rounded-md sm:w-[200px] p-3 flex flex-col gap-3">
                <h4 className="txt-small-bold text-text-secondary">
                  New product tax code
                </h4>
                <Badge size="sm" className="max-w-[174px] truncate">
                  {taxCode}
                </Badge>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={handleCancel} size="sm">
              Cancel
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={handleApplyChange}
              size="sm"
            >
              Apply change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesTaxProductTaxCodeEditModal;
